import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {findIndex, get, has} from 'lodash';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {Widget} from '@ispm/widgets/decorator/widget.decorator';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import {FormGroup} from '@angular/forms';
import {combineLatest, Observable, Subject} from 'rxjs';
import {IspmHttpClient} from '@ispm/core/services/ispm-http-client';
import {Store} from '@ngrx/store';
import {getSelectedApp} from '../../../../ispm-admin/app/org-admin-settings/settings/store/org-admin-settings.selectors';
import {filter, map, startWith, withLatestFrom} from 'rxjs/operators';
import {IAppBrandData} from '../../../../app/brands/models';
import {USER_ACTIONS} from '@ispm/user/store/user-actions.constant';
import {BrandsService} from '../../../../app/brands/services';
import {ConfirmModalLoaderPayload} from "@ispm/layout/classes/confirm-modal-loader-payload";
import {modalResponse} from "@ispm/layout/constants/modal-responses.enum";
import {ModalLoaderNgjsService} from "@ispm/layout/services/modal-loader-ngjs.service";
import {TranslateService} from "@ngx-translate/core";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

export const RESOURCE_ACTIONS = {
  CLONE_ACTION: 'clone',
  EXTEND_ACTION: 'extend'
};

@Component({
  selector: 'ispm-app-configuration-widget',
  templateUrl: './app-configuration-widget.component.html',
  styleUrls: ['./app-configuration-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('AppConfiguration')
export class AppConfigurationWidgetComponent implements OnInit, IWidgetItem {

  public widgetPayload: any;
  public controls: any;
  public enableEditButton: boolean = false;
  public isEditMode: boolean = false;
  public initData: any;
  private initAppEnabled: boolean;
  public widgetFormInstance: FormGroup;
  private isWidgetDataSet: boolean = false;
  // Application Enabled/Disabled
  public enabled: boolean = false;

   // Permission Schema and Role Enable Disable
  public itemsFormData = { enablePermissions: "false" };
  private itemsFormDisable: boolean = false;

  // Resources id add
  private resources: Array<any>;
  public brandData: string;
  public profiles: Array<any> = [];
  public resourcesData: any;
  public isEditableResourceAvailable: boolean = false;

  private destroyed$: Subject<any> = new Subject();
  widgetUpdateValueCallback: (value) => void;
  widgetSubmitStatusValueCallback: (value: boolean) => void;

  private submitUri = 'appconfiguration/toggle';

  public appBasicData$: Observable<{ appId: string, brand: string, profiles?: Array<any>, appSpecificProperties?: Array<any> }>;
  public formData$: Observable<{ brand: string, profiles?: Array<any> }>;

  public resourceSelectionForm: string = 'ADMIN-APPCONFIGURATION-RESOURCES';
  public basicDataForm: string = 'ADMIN-APPCONFIGURATION-BRANDING';
  public updatedAppSpecProps: Array<any> = [];
  public appSpecProp$: Observable<{ appSpecificProperties: Array<any> }>;
  public originalData: any = {};
  public knowledgeBaseValid: boolean = false;
  public brandValid: boolean = false;

  constructor(private _changeDetector: ChangeDetectorRef,
              private routerManagerService: RouterManagerService,
              private brandsService: BrandsService,
              private store: Store<any>,
              private httpService: IspmHttpClient,
              private _modalLoaderService: ModalLoaderNgjsService,
              private _translate: TranslateService,
              private notificationMessageService: NotificationMessageService) {
  }

  ngOnInit() {
    this.store.select(getSelectedApp)
      .subscribe((app: any) => this.basicDataForm = !!app ?
        get(app, 'basicDataForm', 'ADMIN-APPCONFIGURATION-BRANDING') : 'ADMIN-APPCONFIGURATION-BRANDING'
      );
    this.appBasicData$ = this.store.select('users', 'organizationConfiguration', 'applications')
      .pipe(
        filter(applications => !!applications),
        withLatestFrom(this.store.select(getSelectedApp).pipe(filter(app => !!app))),
        map(([applicationsFromOrg, applicationFromAdmin]: [IAppBrandData[], any]) =>
          applicationsFromOrg.find(app => app.appId === applicationFromAdmin.id)),
      );

    const chosenData$ = this.appBasicData$.pipe(
      filter(data => !!data),
      map(data => ({
        brand: data.brand,
        // Knowledge base is used in specific applications
        profiles: get(data, 'profiles', []).map(profile => ({ ...profile, id: profile.reference})),
        appSpecificProperties: get(data, 'appSpecificProperties', [])
      })),
      startWith(undefined)
    );

    // get data from appconfiguration
    const defaultData$ = this.store.select(getSelectedApp).pipe(
      filter((app: any) => !!app),
      map((app: any) => ({
          brand: app.defaultBrand,
          appSpecificProperties: get(app, 'appSpecificProperties', [])
        }
      )),
      startWith(undefined)
    );
    this.formData$ = combineLatest([chosenData$, defaultData$]).pipe(
      map(([chosenData, defaultData]) => chosenData || defaultData)
    );
    this.appSpecProp$ = combineLatest(chosenData$, defaultData$).pipe(
      map(([chosenData, defaultData]) => {
        if (!chosenData && !defaultData) {
          return undefined;
        }
        if(!!defaultData && !!defaultData.appSpecificProperties && defaultData.appSpecificProperties.length == 0){
          this.knowledgeBaseValid = true;
        }
        if (!!defaultData && !!defaultData.appSpecificProperties && defaultData.appSpecificProperties.length > 0
          && (!chosenData || !chosenData.appSpecificProperties || chosenData.appSpecificProperties.length === 0)) {
            return defaultData.appSpecificProperties;
        }

        const len = chosenData.appSpecificProperties.length;
        return [chosenData.appSpecificProperties[len-1]];
      }));
    this.originalData['appSpecProp']= this.appSpecProp$;
    this.originalData['formData']= this.formData$;
  }

  updateValidate(valid: boolean, formIdentifier: number) {
    const formMap = {
      0: 'knowledgeBaseValid',
      1: 'brandValid'
      // Add more mappings as needed
    };
  
    const propertyName = formMap[formIdentifier];
  
    if (propertyName) {
      this[propertyName] = valid;
    }
  }
  

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    this.widgetPayload = get(widgetConfig, 'widgetPayload', { });
    this.enableEditButton = get(widgetConfig.widgetPayload, 'enableEditButton', false);
    this.controls = get(widgetConfig.widgetPayload, 'controls', []);
  }

  set widgetData(widgetData: any) {
    if (widgetData && widgetData.AppConfiguration && !this.isWidgetDataSet) {
      this.initData = widgetData;
      this.isWidgetDataSet = true;
      if (this.initData.Organization) {
        const applications = this.initData.Organization.applications;
        if (applications && applications.length > 0) {
          const item = applications.find( el => el.appId === this.initData.AppConfiguration.id);
          this.enabled = !!item;
          this.originalData['enabled'] =  this.enabled;
          this.initAppEnabled = this.enabled;
        }
        this.resourcesData = this.createResources();
        this._changeDetector.markForCheck();
      }
    }
  }

  set widgetFormInstanceSubmitted(widgetFormInstanceSubmitted: boolean) {
    if (widgetFormInstanceSubmitted) {
      this.submitForm();
    }
  }

  public toggle() {
    this.enabled = !this.enabled;
    this.setItemsFormData();
  }

  setItemsFormData() {
    if(!this.initAppEnabled) {
      this.itemsFormData = { enablePermissions: "true" };
    }
  }
  updateItemsFormData($event: any) {
    if ($event.updated.includes('enablePermissions')) {
      this.itemsFormData.enablePermissions = $event.form.enablePermissions;
      this.originalData['itemsFormData'] = this.itemsFormData;
    }
  }

  updateResourcesData($event: any) {
    this.resources = $event;
  }

  onUpdateData($event) {
    if ($event.updated.includes('brand') || has($event, 'brand')) {
      this.brandData = get($event.form, 'brand.code') || get($event,'brand');
    }
    if ($event.updated.includes('profiles') && get($event.form, 'profiles')) {
      this.profiles = [...(get($event.form, 'profiles', []))]
        .map(profile => {
          return {
            reference: profile.id,
            resourceType: profile.resourceType || 'AppConfiguration',
            text: profile.text
          };
        });
    }
  }

  onUpdatedAppSpecificProperty($event) {
    if ($event.updated.includes('payload')) {
      const elIndex = findIndex(this.updatedAppSpecProps, (o) => o.name === get($event.form, 'name', ''));
      if (elIndex >= 0) {
        this.updatedAppSpecProps[elIndex].payload = get($event.form, 'payload');
      }
      else {
        this.updatedAppSpecProps.push(get($event, 'form'));
      }
    }
  }

  createResources() {
    if (!this.initData.AppConfiguration.resources) {
      return null;
    }
    // The assumption is: only one resource in the resources list can have 'none' id
    return this.initData.AppConfiguration.resources
      .filter((resource) => resource.id === 'none' &&
        (resource.action === RESOURCE_ACTIONS.EXTEND_ACTION || resource.action === RESOURCE_ACTIONS.CLONE_ACTION))
      .map((resource) => {
        this.isEditableResourceAvailable = true;
        this.resourceSelectionForm = get(resource, 'formId', 'ADMIN-APPCONFIGURATION-RESOURCES');
        resource.ids = null;
        return resource;
      });
  }

  submitForm() {
    const resourceArray = [];
    if (this.resources) {
      this.resources.filter(resource => resource.ids).forEach(resource => {
        resource.ids.forEach( el => resourceArray.push({ ...resource, id: el.id}));
      });
      this.resources.filter(resource => resource.payload)
        .forEach(resource => resourceArray.push(resource));
    }
    const data = {
      appId: this.initData.AppConfiguration.id,
      orgId: this.initData.Organization.id,
      enabled: this.enabled,
      enablePermissions: this.itemsFormData.enablePermissions === 'true',
      permissions: [],
      roles: [],
      resources: resourceArray,
      brand: this.brandData,
      profiles: this.profiles,
      appSpecificProperties: this.updatedAppSpecProps
    };

    this.httpService.post(this.submitUri, data).subscribe(
      result => {
        if (this.isEditMode) {
          this.isEditMode = false;
          this._changeDetector.markForCheck();
        }
        this.notificationMessageService.success('Application configuration submission success');
        console.log('Application configuration submit succeeded', result);
        this.widgetSubmitStatusValueCallback.apply(this, [false]);
        this.store.dispatch({
          type: USER_ACTIONS.UPDATE_ORGANIZATION_CONFIGURATION_APPLICATIONS,
          payload: result.data.applications
        });
      },
      error => {
        this.widgetSubmitStatusValueCallback.apply(this, [false]);
        this.notificationMessageService.error('Application configuration submission error; please retry by clicking \'Save\' again');
        console.log('Application configuration update error', error);
      }
    );
  }

  buttonClick(type:string) {
    switch (type) {
      case 'submit':
        this.widgetFormInstanceSubmitted = true;
        break;
      case 'edit':
        this.isEditMode = true;
        this._changeDetector.markForCheck();
        break;
      case 'cancel':
        this.cancelClick();
        break;
    }
  }
  cancelClick() {
    this._modalLoaderService.popupModal('confirm',
      ConfirmModalLoaderPayload.builder()
        .setTitle(this._translate.instant('DISCARD_DATA') || 'Discard Data')
        .setContent(this._translate.instant('ADMIN_UN_SAVED_CHANGES') || 'Canceling will return without saving your changes. Are you sure?')
        .setDeclineButtonText(this._translate.instant('No') || 'No')
        .setApproveButtonText(this._translate.instant('Yes') || 'Yes'),
      'INFO').subscribe(response => {
      if (get(response, 'payload') === modalResponse.approve) {
        this.resetData();
        this.isEditMode = false;
        this._changeDetector.markForCheck();
      }
    });
  }

  public resetData() {
    this.enabled = this.originalData['enabled'];
    this.appSpecProp$ = this.originalData['appSpecProp'] ;
    this.formData$ = this.originalData['formData'];
    this.itemsFormData = this.originalData['itemsFormData'];
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this._changeDetector.detach();
  }
}
